import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class Footer extends i18nMixin(NeptuneElement) {
    templateMarkup = `
        <footer>
            <span>© Ecolab 2026 Inc. All Rights Reserved.</span>
            <span id="separator">|</span>
            <div id="linksContainer">
                <a href="https://www.ecolab.com/sds" target="_blank">Safety Data Sheets</a>
                <span>|</span>
                <a href="https://www.ecolab.com/privacy-policy" target="_blank" >Privacy Policy</a>
                <span>|</span>
                <a href="https://www.ecolab.com/terms-of-use" target="_blank">Terms of Use</a>
            </div>
            <a id="companyLogoLink" href="https://www.ecolab.com/" target="_blank">
                <img alt="Company Logo" src="/src/components/chrome/assets/ecolab-logo/ecolab-logo.svg" />
            </a>
        </footer>
    `;

    componentCssUrl = '/src/components/chrome/footer/footer.css';

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        const culture = this.getAttribute('culture');
        if (culture) { i18n.setLocale(culture); }
        this.#applyTranslations();
        this.updateSidenavClass();
        document.addEventListener('sidenav-ready', this.#handleSidenavReady.bind(this));
        document.addEventListener('sidenav-removed', this.#handleSidenavRemoved.bind(this));
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
        document.removeEventListener('sidenav-ready', this.#handleSidenavReady.bind(this));
        document.removeEventListener('sidenav-removed', this.#handleSidenavRemoved.bind(this));
    }
    /**
     * @description Called when the locale changes. This method is part of the i18nMixin and is triggered whenever the active locale is updated.
     * It ensures that the footer content is re-rendered with the appropriate translations for the new locale.
     * This method is automatically called by the i18nMixin when the locale changes, so you don't need to call it manually.
     * It will update the copyright text, link labels, and logo alt text based on the current locale.
     * If a translation is missing, it will fall back to the default English messages or the messages defined in the locale JSON files.
     */
    onLocaleChange() {
        this.#applyTranslations();
    }

    /**
     * @description Applies translations to the footer content based on the current locale.
     * It updates the copyright text, link labels, and logo alt text with the appropriate translations.
     * If a translation is missing for a specific locale, it will fall back to the default English messages or the messages defined in the locale JSON files.
     * This method is called whenever the active locale changes, ensuring that the footer content is always displayed in the correct language.
     * Note: Ensure that the translations for the footer content are available in the locale JSON files for proper rendering.
     * If a translation is missing, it will fall back to the default English messages or the messages defined in the locale JSON files.
     */
    #applyTranslations() {
        const year = new Date().getFullYear();
        const copyright = this.shadowRoot?.querySelector('footer > span:first-child');
        const safetyLink = this.shadowRoot?.querySelector('#linksContainer a:nth-of-type(1)');
        const privacyLink = this.shadowRoot?.querySelector('#linksContainer a:nth-of-type(2)');
        const termsLink = this.shadowRoot?.querySelector('#linksContainer a:nth-of-type(3)');
        const logoImg = this.shadowRoot?.querySelector('footer > a > img');

        if (copyright) { copyright.textContent = `© ${this.translate('FOOTER.COPYRIGHT')} ${year} ${this.translate('FOOTER.ALL_RIGHTS_RESERVED')}`; }
        if (safetyLink) { safetyLink.textContent = this.translate('FOOTER.LINKS.SAFETY_DATA_SHEETS'); }
        if (privacyLink) { privacyLink.textContent = this.translate('FOOTER.LINKS.PRIVACY_POLICY'); }
        if (termsLink) { termsLink.textContent = this.translate('FOOTER.LINKS.TERMS_OF_USE'); }
        if (logoImg) { logoImg.alt = this.translate('FOOTER.LOGO.ALT'); }
    }

    updateSidenavClass() {
        const hasSidenav = document.querySelector('neptune-sidenav');
        this.classList.toggle('has-sidenav', !!hasSidenav);
    };

    #handleSidenavReady(){
        this.classList.add('has-sidenav');
    };

    #handleSidenavRemoved() {
        this.classList.remove('has-sidenav');
    };

    static get observedAttributes() {
        return ['culture'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) { return; }
        if (name === 'culture' && newValue) { i18n.setLocale(newValue); }
    }
}

customElements.define('neptune-footer', Footer);
export default Footer;